import { LocationPageInterface } from "@swivl-mono/types";
import { Input, SearchInput } from "@swivl-mono/ui";
import { useEffect, useMemo, useState } from "react";
import { IconType } from "react-icons";
import { FaSearch } from "react-icons/fa";
import { useLocation } from "react-router-dom";

import { useWorksheetCustomizationCounts } from "../../Hooks/useCustomizationCounts";
import { useViewCustomizations } from "../../Hooks/useViewCustomizations";
import { Member } from "../../Models/Member/Member.Current";
import { ShowIf } from "../ShowIf/ShowIf";
import { RouterWorksheetData } from "../Worksheets/Types/Worksheet.Interface";
import { SidebarLink } from "./SidebarLink";
import { SidebarSectionHeading } from "./SidebarSectionHeading";

interface Props {
  items: RouterWorksheetData[];
  prefix?: string;
  suffix?: string;
  page?: LocationPageInterface;
  hideIf?: any;
  /** Hides the local "Search..." filter box (Details v3 uses global search instead). */
  hideSearch?: boolean;
}

export function SearchableSidebarLinksList(props: Props) {
  const [search, setSearch] = useState("");
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const [viewCustomizations] = useViewCustomizations();
  const rawCounts = useWorksheetCustomizationCounts(props.items, props.page?.groupId, props.page?.locationId);
  const sectionCounts = viewCustomizations ? rawCounts : {};

  useEffect(() => {
    if (!props.items?.length) return;

    const sectionsToOpen: Record<string, boolean> = {};
    props.items.forEach((item) => {
      if (item.children?.length && location.pathname.includes(`/${item.to}/`)) {
        sectionsToOpen[item.to] = true;
      }
    });

    if (Object.keys(sectionsToOpen).length > 0) {
      setOpenSections((prev) => ({ ...prev, ...sectionsToOpen }));
    }
  }, [props.items, location.pathname]);

  useEffect(() => {
    if (!search) return;

    const newOpenSections: Record<string, boolean> = {};
    props.items.forEach((item) => {
      const isParentMatch = item.label.toLowerCase().includes(search.toLowerCase());
      const matchingChildren =
        item.children?.filter((child) => {
          return child.label.toLowerCase().includes(search.toLowerCase());
        }) ?? [];

      if (isParentMatch || matchingChildren.length > 0) {
        newOpenSections[item.to] = true;
      }
    });
    setOpenSections(newOpenSections);
  }, [search, props.items]);

  const filtered = useMemo(() => {
    if (!props.items) return [];

    const isStaff = Member.isSwivlStaff;
    const searchLower = search.toLowerCase();
    return props.items
      .filter((item) => !item.swivlStaffOnly || isStaff)
      .map((item) => {
        const isParentMatch = item.label.toLowerCase().includes(searchLower);

        const matchingChildren =
          item.children?.filter((child) => {
            return child.label.toLowerCase().includes(searchLower);
          }) ?? [];

        if (isParentMatch) {
          return {
            ...item,
            children: item.children ?? []
          };
        }

        if (matchingChildren.length > 0) {
          return {
            ...item,
            children: matchingChildren
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [props.items, search]);

  const handleToggle = (to: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [to]: !prev[to]
    }));
  };

  const handleClear = () => {
    setSearch("");
  };

  return (
    <div className="SearchableSidebarLinksList">
      {!props.hideSearch && (
        <div className="p-3">
          <SearchInput placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} onClear={handleClear} />
        </div>
      )}
      {filtered &&
        filtered.map((item) => {
          if (item?.children && item.children.length > 0) {
            const to = `${props.prefix || ""}/${item.to}`;
            if (item.apiScope) {
              return (
                <ShowIf key={to} hideIf={item.hideIf} scope={item.apiScope} inGroupId={props.page?.groupId} inLocationId={props.page?.locationId}>
                  <SidebarSectionHeading
                    hideIf={item.hideIf}
                    key={to}
                    openOnPath={to}
                    isOpen={openSections[item.to]}
                    onToggle={() => handleToggle(item.to)}
                    items={item.children}
                    customizationCount={sectionCounts[item.to]}
                    sectionCounts={sectionCounts}
                    sectionTo={item.to}
                  >
                    {item.label}
                  </SidebarSectionHeading>
                </ShowIf>
              );
            }
            return (
              <SidebarSectionHeading
                key={to}
                openOnPath={to}
                hideIf={item.hideIf}
                onToggle={() => handleToggle(item.to)}
                isOpen={openSections[item.to]}
                items={item.children}
                locationId={props.page?.locationId}
                groupId={props.page?.groupId}
                customizationCount={sectionCounts[item.to]}
                sectionCounts={sectionCounts}
                sectionTo={item.to}
              >
                {item.label}
              </SidebarSectionHeading>
            );
          }

          const to = `${props.prefix || ""}/${item?.to}${props.suffix ? `/${props.suffix}` : ""}`;
          if (item && item?.apiScope) {
            return (
              <ShowIf key={to} hideIf={item?.hideIf} scope={item.apiScope} inGroupId={props.page?.groupId} inLocationId={props.page?.locationId}>
                <SidebarLink
                  hideIf={item?.hideIf}
                  height={38}
                  groupId={props.page?.groupId}
                  locationId={props.page?.locationId}
                  key={to}
                  to={to}
                  customizationCount={item?.to ? sectionCounts[item.to] : undefined}
                >
                  {item.label}
                </SidebarLink>
              </ShowIf>
            );
          }
          return (
            <SidebarLink
              hideIf={item?.hideIf}
              height={38}
              groupId={props.page?.groupId}
              locationId={props.page?.locationId}
              key={to}
              to={to}
              customizationCount={item?.to ? sectionCounts[item.to] : undefined}
            >
              {item?.label}
            </SidebarLink>
          );
        })}
    </div>
  );
}
